import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { NOT_FOUND_HEAD_FALLBACK } from "@/lib/brandIcons";
import { blogIndexHead } from "@/lib/blog/seo";
import { BlogShell } from "#/components/blog/BlogShell";
import { BLOG_MEASURE, BlogPageHeader } from "#/components/blog/BlogPageHeader";
import { PostCard } from "#/components/blog/PostCard";

// Per-request cache for the rendered page: short shared-cache TTL with a long
// stale-while-revalidate so the CDN serves instantly and refreshes in the
// background (issue AC). Only applied to a 200 (loaderData present) — never to a
// notFound()/404, so a not-yet-configured or not-yet-published URL can't be
// edge-cached as missing for a day.
const CACHE = "public, s-maxage=60, stale-while-revalidate=86400";

// Path A: read Postgres directly on the SSR server. The handler dynamically imports
// the server-only query module so pg / firebase-functions stay out of the client
// bundle (and route-option tests can import this file). An unset dogfood workspace
// → undefined → 404.
const getBlogIndex = createServerFn({ method: "GET" }).handler(async () => {
  const { loadBlogIndex, resolveDogfoodWorkspaceId } = await import(
    "@/lib/blog/queries"
  );
  const workspaceId = resolveDogfoodWorkspaceId();
  return workspaceId ? await loadBlogIndex(workspaceId) : undefined;
});

export const Route = createFileRoute("/blog/")({
  ssr: true,
  loader: async () => {
    const data = await getBlogIndex();
    if (!data) throw notFound();
    return data;
  },
  headers: ({ loaderData }) =>
    loaderData
      ? { "cache-control": CACHE }
      : { "cache-control": "no-store" },
  // notFound()-branch icon fallback + the `as never` cast it needs: see
  // `NOT_FOUND_HEAD_FALLBACK`'s docblock (#1099 / US2).
  head: ({ loaderData }) =>
    (loaderData
      ? blogIndexHead(loaderData.blog, loaderData.canonical, loaderData.feedUrl)
      : NOT_FOUND_HEAD_FALLBACK) as never,
  component: BlogIndexPage,
});

function BlogIndexPage() {
  const { blog, posts } = Route.useLoaderData();
  return (
    <BlogShell>
      <BlogPageHeader title={blog.title} lede={blog.description} />

      <section className="py-14">
        <div className={BLOG_MEASURE}>
          {posts.length === 0 ? (
            <p className="text-[color:var(--gs-muted)]">No posts yet.</p>
          ) : (
            <ul className="grid gap-6">
              {posts.map((post) => (
                <PostCard key={post.postId} post={post} />
              ))}
            </ul>
          )}
        </div>
      </section>
    </BlogShell>
  );
}
